import React, { useEffect, useState } from "react";
import { useMutation, gql, useQuery } from "@apollo/client";
import Header from "../../../../../components/common/header";
import { useNavigate } from "react-router-dom";
import LoadingSpinnerFull from "../../../../../components/common/loading-spinner/loading-spinner-full";
import BottomSheet from "../../../../../components/common/bottom-sheet";

const bankList = [
  "국민은행",
  "우리은행",
  "신한은행",
  "기업은행",
  "하나은행",
  "농협은행",
  "지역농축협",
  "SC은행",
  "한국씨티은행",
  "우체국",
  "카카오뱅크",
  "토스뱅크",
  "케이뱅크",
  "경남은행",
  "광주은행",
  "IM[구 대구은행]",
  "부산은행",
  "산림조합",
  "산업은행",
  "저축은행",
  "새마을금고",
  "수협",
  "신협",
  "전북은행",
  "제주은행",
];

const GET_SELLER_INFO = gql`
  query GetSellerSettlementInfo {
    seller {
      bank
      accountHolder
      accountNumber
      businessNumber
      businessType
      representativeName
      email
      contactNumber
      tossPhone
      tossPaymentsSellerId
      tossSellerStatus
    }
  }
`;

const UPDATE_SETTLEMENT_INFO = gql`
  mutation UpdateSettlementInfo(
    $bank: String
    $accountHolder: String
    $accountNumber: String
    $businessNumber: String
    $businessType: String
    $representativeName: String
    $email: String
    $tossPhone: String
  ) {
    updateSettlementInfo(
      bank: $bank
      accountHolder: $accountHolder
      accountNumber: $accountNumber
      businessNumber: $businessNumber
      businessType: $businessType
      representativeName: $representativeName
      email: $email
      tossPhone: $tossPhone
    ) {
      ok
      error
      tossRegistrationStatus
    }
  }
`;

const INITIATE_TOSS_REGISTRATION = gql`
  mutation InitiateTossRegistration {
    initiateTossRegistration {
      ok
      error
      tossSellerStatus
    }
  }
`;

const BUSINESS_TYPE_OPTIONS = [
  { value: "INDIVIDUAL_BUSINESS", label: "개인사업자" },
  { value: "CORPORATE", label: "법인사업자" },
];

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export default function SettlementInfoUpdate() {
  const navigate = useNavigate();
  const { data, loading: queryLoading } = useQuery(GET_SELLER_INFO, {
    fetchPolicy: "network-only",
  });

  const [bank, setBank] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [email, setEmail] = useState("");
  const [tossPhone, setTossPhone] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // 바텀시트 상태
  const [isConfirmSheetOpen, setIsConfirmSheetOpen] = useState(false);
  const [isSuccessSheetOpen, setIsSuccessSheetOpen] = useState(false);

  const [updateSettlementInfo, { loading: mutationLoading }] = useMutation(
    UPDATE_SETTLEMENT_INFO,
  );
  const [initiateTossRegistration, { loading: tossLoading }] = useMutation(
    INITIATE_TOSS_REGISTRATION,
  );

  const digitsOnly = (value: string) => value.replace(/[^0-9]/g, "");

  const isEditMode = Boolean(data?.seller?.bank);
  const isAlreadyRegistered = Boolean(data?.seller?.tossPaymentsSellerId);
  const tossSellerStatus = data?.seller?.tossSellerStatus as
    | string
    | null
    | undefined;
  /** 신규(토스 미등록) 또는 본인인증 대기 → 저장 후 initiateTossRegistration(바텀시트) 허용. 그 외(KYC 등)는 DB 저장만 */
  const tossRequiresRegistrationFlowAfterSave =
    !isAlreadyRegistered || tossSellerStatus === "APPROVAL_REQUIRED";
  // 번호 입력 필드 표시 조건:
  // 1. 아직 토스에 등록되지 않은 신규 셀러
  // 2. tossPhone이 아직 저장되지 않은 셀러
  // 3. 인증 대기(APPROVAL_REQUIRED) 상태 — 번호 변경 후 재시도 허용
  const needsPhoneInput =
    !isAlreadyRegistered ||
    !data?.seller?.tossPhone ||
    tossSellerStatus === "APPROVAL_REQUIRED";

  const isMobileNumber = tossPhone.startsWith("010");
  const hasPhoneInput = tossPhone.length > 0;

  useEffect(() => {
    if (data?.seller) {
      const s = data.seller;
      setBank(s.bank || "");
      setAccountHolder(s.accountHolder || "");
      setAccountNumber(digitsOnly(s.accountNumber || ""));
      setBusinessNumber(digitsOnly(s.businessNumber || ""));
      setBusinessType(s.businessType || "");
      setRepresentativeName(s.representativeName || "");
      setEmail(s.email || "");
      // tossPhone 없으면 contactNumber로 pre-fill
      setTossPhone(digitsOnly(s.tossPhone || s.contactNumber || ""));
    }
  }, [data]);

  const handleSubmit = () => {
    setErrorMessage("");

    const normalizedAccountNumber = digitsOnly(accountNumber);
    const normalizedBusinessNumber = digitsOnly(businessNumber);
    const normalizedTossPhone = digitsOnly(tossPhone);

    if (
      !bank ||
      !accountHolder ||
      !normalizedAccountNumber ||
      !normalizedBusinessNumber ||
      !businessType ||
      !representativeName ||
      !email
    ) {
      setErrorMessage("모든 필수 항목을 입력해주세요.");
      return;
    }

    if (needsPhoneInput && !normalizedTossPhone) {
      setErrorMessage("인증에 사용할 휴대폰 번호를 입력해주세요.");
      return;
    }

    updateSettlementInfo({
      variables: {
        bank,
        accountHolder,
        accountNumber: normalizedAccountNumber,
        businessNumber: normalizedBusinessNumber,
        businessType,
        representativeName,
        email,
        tossPhone: normalizedTossPhone || undefined,
      },
    })
      .then((res) => {
        const result = res.data?.updateSettlementInfo;
        if (!result?.ok) {
          setErrorMessage(result?.error || "정산 정보 저장에 실패했습니다.");
          return;
        }
        if (!tossRequiresRegistrationFlowAfterSave) {
          // 토스 연동 단계 없음: DB만 저장 (APPROVED·PARTIALLY_APPROVED·KYC 등)
          alert("정산 정보가 저장되었어요.");
          navigate(-1);
        } else {
          // 신규 등록 또는 APPROVAL_REQUIRED 시 인증 번호 확인 바텀시트
          setIsConfirmSheetOpen(true);
        }
      })
      .catch(() => {
        setErrorMessage("정산 정보 저장 중 오류가 발생했습니다.");
      });
  };

  const handleConfirmTossRegistration = () => {
    setIsConfirmSheetOpen(false);
    initiateTossRegistration()
      .then((res) => {
        const result = res.data?.initiateTossRegistration;
        if (!result?.ok) {
          setErrorMessage(
            result?.error ||
              "인증 문자 발송에 실패했습니다. 다시 시도해 주세요.",
          );
          return;
        }
        setIsSuccessSheetOpen(true);
      })
      .catch(() => {
        setErrorMessage("인증 요청 중 오류가 발생했습니다.");
      });
  };

  if (queryLoading) return <LoadingSpinnerFull />;

  return (
    <div className="w-full min-h-[100dvh] h-full overflow-y-scroll bg-white">
      <Header
        title={isEditMode ? "정산 정보 수정하기" : "정산 정보 입력하기"}
        showBackButton
      />
      <div className="max-w-xl mx-auto bg-white w-full p-6 rounded-lg shadow-md">
        <p className="text-sm text-gray-500 mb-1">
          판매 수익을 안전하게 정산받기 위해 아래 정보를 입력해 주세요.
        </p>
        <p className="text-sm text-gray-600 mb-6">
          * 정산받을 계좌는{" "}
          <span className="text-red-600 font-medium">
            반드시 사업자 등록증의 명의와 동일해야
          </span>{" "}
          합니다.
        </p>

        {/* 계좌 정보 섹션 */}
        <p className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">
          계좌 정보
        </p>

        <div className="mb-4">
          <label className="mb-2 block text-gray-700 font-semibold">
            은행명*
          </label>
          <select
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="" disabled>
              은행을 선택해주세요
            </option>
            {bankList.map((bankName, index) => (
              <option key={index} value={bankName}>
                {bankName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-gray-700 font-semibold">
            계좌번호*{" "}
            <span className="text-gray-400 font-normal text-sm">
              (숫자만 입력)
            </span>
          </label>
          <input
            value={accountNumber}
            onChange={(e) => setAccountNumber(digitsOnly(e.target.value))}
            placeholder="12345678901234"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-gray-700 font-semibold">
            예금주명*
          </label>
          <input
            value={accountHolder}
            onChange={(e) => setAccountHolder(e.target.value)}
            placeholder="홍길동"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        {/* 사업자 정보 섹션 */}
        <p className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">
          사업자 정보
        </p>

        <div className="mb-4">
          <label className="mb-2 block text-gray-700 font-semibold">
            사업자 등록번호*{" "}
            <span className="text-gray-400 font-normal text-sm">
              (숫자만 입력)
            </span>
          </label>
          <input
            value={businessNumber}
            onChange={(e) => setBusinessNumber(digitsOnly(e.target.value))}
            placeholder="1234567890"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-gray-700 font-semibold">
            사업자 유형*
          </label>
          <select
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="" disabled>
              사업자 유형을 선택해주세요
            </option>
            {BUSINESS_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-gray-700 font-semibold">
            대표자명*
          </label>
          <input
            value={representativeName}
            onChange={(e) => setRepresentativeName(e.target.value)}
            placeholder="홍길동"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-gray-700 font-semibold">
            이메일*
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        {/* 본인인증 번호 섹션 (tossPhone 미등록 시 항상 표시) */}
        {needsPhoneInput && (
          <>
            <p className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">
              본인인증
            </p>

            <div className="mb-4">
              <label className="mb-2 block text-gray-700 font-semibold">
                인증받을 휴대폰 번호*
              </label>
              <input
                type="tel"
                value={tossPhone}
                onChange={(e) => setTossPhone(digitsOnly(e.target.value))}
                placeholder="01012345678"
                className={`w-full px-4 py-2 border rounded-lg ${
                  hasPhoneInput && !isMobileNumber
                    ? "border-orange-400 bg-orange-50 text-orange-800"
                    : "border-gray-300"
                }`}
                maxLength={11}
              />

              {/* 010이 아닌 경우 강한 경고 */}
              {hasPhoneInput && !isMobileNumber && (
                <div className="mt-2 flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-300 px-3 py-3">
                  <span className="text-lg leading-none">⚠️</span>
                  <div>
                    <p className="text-sm font-bold text-orange-700">
                      휴대폰 번호를 확인해 주세요
                    </p>
                    <p className="text-xs text-orange-600 mt-0.5">
                      토스페이먼츠 본인인증은{" "}
                      <strong>010으로 시작하는 휴대폰 번호</strong>로만 진행할
                      수 있어요. 현재 입력된 번호(
                      {formatPhoneDisplay(tossPhone)})로는 인증이 어려울 수
                      있어요.
                    </p>
                  </div>
                </div>
              )}

              {/* 010인 경우 안내 */}
              {hasPhoneInput && isMobileNumber && (
                <p className="text-xs text-gray-400 mt-1.5">
                  이 번호로 토스페이먼츠 인증 문자가 발송돼요.
                </p>
              )}

              <p className="text-xs text-gray-400 mt-1">
                가입 시 등록한 번호가 자동으로 입력됐어요. 다를 경우 수정해
                주세요.
              </p>
            </div>
          </>
        )}

        {errorMessage && (
          <div className="text-red-500 text-sm mb-4">{errorMessage}</div>
        )}

        <button
          onClick={handleSubmit}
          className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg disabled:opacity-60"
          disabled={mutationLoading || tossLoading}
        >
          {mutationLoading
            ? "저장 중..."
            : tossLoading
              ? "인증 요청 중..."
              : needsPhoneInput
                ? "저장하고 인증 문자 받기"
                : "저장하기"}
        </button>
      </div>

      {/* 인증 번호 확인 바텀시트 (최초 등록 시) */}
      <BottomSheet
        isOpen={isConfirmSheetOpen}
        onClose={() => setIsConfirmSheetOpen(false)}
        eyebrow="최초 1회 인증이 필요해요"
        mainText="이 번호로 인증을 진행할게요"
        subText={`📱 ${formatPhoneDisplay(tossPhone)}\n\n위 번호로 토스페이먼츠 인증 문자를 보내드려요.\n번호가 맞다면 확인을 눌러주세요.`}
        buttonText="확인"
        onButtonClick={handleConfirmTossRegistration}
        disableDrag
      />

      {/* 인증 문자 발송 완료 바텀시트 */}
      <BottomSheet
        isOpen={isSuccessSheetOpen}
        onClose={() => {
          setIsSuccessSheetOpen(false);
          navigate("/boss/home");
        }}
        eyebrow="최초 1회 인증이 필요해요"
        mainText="📩 인증 문자가 발송됐어요"
        subText={`등록된 번호로 토스페이먼츠 인증 문자를 보내드렸어요.\n문자의 링크를 눌러 인증을 완료해 주세요.`}
        buttonText="확인"
        onButtonClick={() => {
          setIsSuccessSheetOpen(false);
          navigate("/boss/home");
        }}
        disableDrag
      />
    </div>
  );
}
